import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import _ from 'lodash';
import { Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDollarSign,
  faDownload,
  faEdit,
  faEye,
  faPlus,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { getData } from '../../../redux-store/actions';
import {
  CP_GET_LIST,
  POL_GET_MONEDAS,
  POL_GET_PRODUCTOS,
} from '../../../redux-store/constants/action-types';
import {
  ALL_OPTIONS,
  API,
  BASE_URL,
  NO_FILTERED_ID,
  SEARCH_DELAY_MS,
  PROFILES,
  ROLES_ASEGURADO,
  ROLES_CORREDOR,
} from '../../../CONST';
import {
  cellDateOperation,
  cellNumOperation,
  cellOperation,
  headerDoubleRowOperation,
  headerOperation,
  renderPolicyIsVigente,
} from '../../../helpers/tables';
import { buildFilterParams, fileDownload, intermediaryAccess, userHasRole } from '../../../helpers/helpers';
import BaseTable from '../../shared/BaseTable';
import FilterSelect from '../../shared/FilterSelect';
import { objectsToOptions } from '../../../helpers/selects';
import DownloadBtn from '../../shared/DownloadBtn';
import isUserHasProfile from '../../../helpers/profileValidator';
import { LINEA_CREDITO_WRITE } from '../../../helpers/profilePermission';
import LoadingSpinner from '../../shared/LoadingSpinner';

class CreditPolicyList extends Component {
  constructor(props) {
    super(props);
    this.filtering = false;
    this.filterUrl = '';
    this.fetchDataWithDebounce = _.debounce(this.fetchData, SEARCH_DELAY_MS);
    this.tableRef = React.createRef();

    this.state = {
      creditPoliciesLoading: true
    };
  }

  componentDidMount() {
    const { dispatch } = this.props;
    dispatch(getData({
      url: `${BASE_URL}${API.monedas}/`,
      type: POL_GET_MONEDAS,
    }));
    dispatch(getData({
      url: `${BASE_URL}${API.productos}/?tipo_poliza=credito`,
      type: POL_GET_PRODUCTOS,
    }));
  }

  componentDidUpdate(prevProps) {
    const { client: prevClient, loading: prevLoading, creditPolicies: prevCreditPolicies } = prevProps;
    const { client, loading, creditPolicies } = this.props;
    const { creditPoliciesLoading } = this.state;

    if (creditPoliciesLoading && prevLoading && !loading) {
      this.setState({ creditPoliciesLoading: false });
    }

    if (creditPoliciesLoading && creditPolicies?.data && !prevCreditPolicies?.data) {
      this.setState({ creditPoliciesLoading: false });
    }

    if (JSON.stringify(prevClient) !== JSON.stringify(client) && this.tableRef.current) {
      this.fetchStrategy(this.tableRef.current.state);
    }
  }

  onFilteredChange = () => {
    this.filtering = true;
  };

  fetchStrategy = (tableState) => {
    if (this.filtering) {
      return this.fetchDataWithDebounce(tableState);
    }
    return this.fetchData(tableState);
  };

  fetchData = (state) => {
    const { dispatch, user, client } = this.props;
    this.filtering = false;

    let filterParams = buildFilterParams(
      state,
      ['balance__inicio_vigencia', 'balance__termino_vigencia'],
      ['vigentes'],
    );

    if (user.is_orsan && client) {
      filterParams = `${filterParams}client_id=${client.id}`;
    }

    if (intermediaryAccess(user) && client) {
      filterParams = `${filterParams}client_id=${client.id}&intermediario_id=${user.organization}`;
    }

    const filterUrl = `${BASE_URL}${API.creditos}?${filterParams}`;

    // only fetch data if filters have changed
    if (filterUrl !== this.filterUrl) {
      this.filterUrl = filterUrl;

      dispatch(getData({
        url: filterUrl,
        type: CP_GET_LIST,
      }));
    }

  };

  render() {
    const {
      creditPolicies,
      loading,
      loginIdToken,
      user,
      client,
      poliza: {
        monedas,
        productos
      },
    } = this.props;
    const { creditPoliciesLoading } = this.state;

    const isIntermediary = intermediaryAccess(user);
    const isAseguradoOnly = userHasRole(user, ROLES_ASEGURADO) && !userHasRole(user, ROLES_CORREDOR);

    const spinnerLoading = isAseguradoOnly && creditPoliciesLoading;
    const hasAccion = (slug) => {
      if (!user.acciones_permitidas?.acciones) return false;

      return user.acciones_permitidas.acciones.some(
        accion => 
          accion.applies && 
          accion.is_active && 
          accion.accion.slug === slug
      );
    };

    const canAccessLibrosVenta = () => {
      const isIntermediarioCredito = user.roles?.includes('intermediariocredito');

      if (isIntermediarioCredito) {
        return hasAccion('ver-modulo-libro-venta');
      }

      return true;
    };
    
    const canAccessLibrosVentaForPolicy = (policyNemotecnico) => {
      const isIntermediarioCredito = user.roles?.includes('intermediariocredito');

      if (isIntermediarioCredito) {
        return user.acciones_permitidas?.acciones?.some(
          accion => 
            accion.applies && 
            accion.is_active && 
            accion.accion.slug === 'ver-modulo-libro-venta' &&
            accion.policy_nemotecnico === policyNemotecnico
        ) || false;
      }

      return true;
    };

    if (monedas.length === 0 || productos.length === 0) {
      return null
    }

    const monedasOptions = objectsToOptions(monedas, 'nombre_corto', true);
    const productosOptions = objectsToOptions(productos, 'descripcion', true);
    const xlsDownloadUrl = `${BASE_URL}${API.creditos}/download-xlsx`;
    const pdfDownloadUrl = `${BASE_URL}${API.creditos}/download-pdf`;
    const columns = [
      {
        Header: headerDoubleRowOperation('Número de', 'Póliza'),
        id: 'base__nemotecnico__icontains',
        accessor: i => cellOperation(i.nemotecnico),
        sortable: false,
        minWidth: 120,
      },
      {
        Header: headerOperation('Vigente'),
        id: 'vigentes',
        accessor: i => {
          const balanceData = i.balance || i.borrador;
          return cellOperation(renderPolicyIsVigente(balanceData));
        },
        Filter: obj => (
          <FilterSelect
            onFilterChange={obj.onChange}
            filter={obj.filter}
            options={[{ label: 'Sí', value: '1' }, { label: 'No', value: '0' }]}
            defaultOption={{ label: 'Todas', value: ALL_OPTIONS }}
          />
        ),
        minWidth: 100,
        sortable: false,
      },
      {
        Header: headerOperation('Producto'),
        id: 'base__producto_id',
        accessor: i => cellOperation(i.producto, 'left'),
        Filter: obj => (
          <FilterSelect
            filter={obj.filter}
            defaultOption={{ label: 'Todos', value: NO_FILTERED_ID }}
            onFilterChange={obj.onChange}
            options={productosOptions}
          />
        ),
        sortable: false,
        minWidth: 150,
      },
      {
        Header: headerOperation('Moneda'),
        id: 'base__moneda_asegurada_id',
        accessor: i => cellOperation(i.moneda),
        Filter: obj => (
          <FilterSelect
            onFilterChange={obj.onChange}
            filter={obj.filter}
            options={monedasOptions}
            defaultOption={{ label: 'Todas', value: NO_FILTERED_ID }}
          />
        ),
        sortable: false,
        minWidth: 120,
      },
      {
        Header: headerDoubleRowOperation('Estimación de', 'Ventas'),
        id: 'balance__estimacion_ventas_amt',
        accessor: i => {
          const balanceData = i.balance || i.borrador;
          return cellNumOperation(balanceData?.estimacion_ventas_amt, 'float');
        },
        filterable: false,
        sortable: false,
        minWidth: 100,
      },
      {
        Header: headerOperation('Tasa'),
        id: 'balance__tasa_costo_seguro_defecto_pct',
        accessor: i => {
          const balanceData = i.balance || i.borrador;
          return cellNumOperation(balanceData?.tasa_costo_seguro_defecto_pct, 'float', '%');
        },
        filterable: false,
        sortable: false,
        minWidth: 100,
      },
      {
        Header: headerDoubleRowOperation('Prima Neta', 'Estimada'),
        id: 'balance__prima_minima_anual_amt',
        accessor: i => {
          const balanceData = i.balance || i.borrador;
          return balanceData?.prima_minima_anual_amt ? cellNumOperation(balanceData.prima_minima_anual_amt, 'float') : '-';
        },        
        filterable: false,
        sortable: false,
        minWidth: 100,
      },
      {
        Header: headerOperation('Inicio Cobertura'),
        id: 'balance__inicio_vigencia',
        accessor: i => cellDateOperation(i.inicio_vigencia),
        sortable: false,
        minWidth: 110,
      },
      {
        Header: headerOperation('Fin Cobertura'),
        id: 'balance__termino_vigencia',
        accessor: i => cellDateOperation(i.termino_vigencia),
        sortable: false,
        minWidth: 110,
      },
      ...((isUserHasProfile(LINEA_CREDITO_WRITE) && user.is_orsan) || isIntermediary ? [{
        Header: headerDoubleRowOperation('Cartera de', 'Clientes'),
        id: 'cartera_clientes',
        // Se habilita opcion agregar y quitar a perfil corredor
        accessor: i => cellOperation(((isUserHasProfile(LINEA_CREDITO_WRITE) && user.is_orsan) || isIntermediary) && (
          <div
            className="table-cell"
            style={{ textAlign: 'center' }}
          >
            {(user.is_orsan || isIntermediary) && (
              <span title="editar">
                <Link
                  to={{
                    pathname: '/asegurado/lineas-credito/',
                    search: `?poliza_id=${i.id}`,
                    state: { returnTo: '/asegurado/polizas-credito' },
                  }}
                  className="table-link policy"
                >
                  <FontAwesomeIcon icon={faEdit} />
                </Link>
              </span>
            )}

            {(user.is_orsan || isIntermediary) && (
              <>
                &nbsp;&nbsp;
                <span title="agregar">
                  <Link
                    to={{
                      pathname: `/asegurado/linea-credito/${i.id}/`,
                      state: { returnTo: '/asegurado/polizas-credito' },
                    }}
                    className="table-link policy"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </Link>
                </span>
              </>
            )}
          </div>
        )),
        sortable: false,
        filterable: false,
        minWidth: 100,
      }] : []),
      ...(canAccessLibrosVenta() ? [{
          Header: headerDoubleRowOperation('Libros', 'de Venta'),
          id: 'libro_ventas',
          Cell: (row) => {
            const canAccess = canAccessLibrosVentaForPolicy(row.original.nemotecnico);
            
            return cellOperation(
              (isUserHasProfile(LINEA_CREDITO_WRITE) || !user.is_orsan || isIntermediary) && 
              canAccess && (
                <Link
                  to={`/asegurado/libros-venta/${row.original.id}`}
                  className="table-link"
                >
                  Visualizar
                </Link>
              )
            );
          },
          sortable: false,
          filterable: false,
          minWidth: 100,
        }] : []),
      (isUserHasProfile([PROFILES.externoAdministradorAseguradoCredito, PROFILES.ceo, PROFILES.administradorTI])) && {
        Header: headerOperation('Prórrogas'),
        id: 'prorrogas',
        Cell: (row) => cellOperation((
          <>
            {/* TODO: Aclarar bien cuáles van a ser los perfiles para acceder a prórrogas. DEJO COMENTADO COMO ESTABA ANTES EL IF, se cambia a este para desarrollo */}
            {/*{!isAsegurado(user) && isUserHasProfile([PROFILES.ceo, PROFILES.administradorTI, PROFILES.visualizacionProrrogasCredito]) && (*/}
            {isUserHasProfile([PROFILES.externoAdministradorAseguradoCredito, PROFILES.ceo, PROFILES.administradorTI]) && (
              <span title="solicitar prórroga">
                <Link
                  to={`/prorroga/solicitud-prorroga/${row.original.id}`}
                  className="table-link"
                >
                  <FontAwesomeIcon icon={faEdit} />
                </Link>
              </span>
            )}
            &nbsp;&nbsp;
            {user.is_orsan && (
              <span title="ver prórrogas">
                <Link
                  to={`/prorroga/solicitudes-prorroga/${row.original.id}`}
                  className="table-link"
                >
                  <FontAwesomeIcon icon={faSearch} />
                </Link>
              </span>
            )}
          </>
        )),
        sortable: false,
        filterable: false,
        minWidth: 130,
      },
      {
        Header: headerOperation('Acciones'),
        id: 'actions',
        accessor: i => (
          <div
            className="table-cell"
            style={{ textAlign: 'center' }}
          >
            <span title="ver">
              <Link
                to={{
                  pathname: `/asegurado/polizas-credito/${i.id}`,
                  state: { returnTo: "/asegurado/polizas-credito/" },
                }}
                className="table-link policy"
              >
                <FontAwesomeIcon icon={faEye} />
              </Link>
            </span>
            &nbsp;&nbsp;
            <span title="descargar">
              <Button
                className="download"
                style={{ verticalAlign: 'baseline' }}
                onClick={() =>
                  fileDownload(`${BASE_URL}${API.creditos}/${i.id}/download-endoso`, loginIdToken)
                }
              >
                <FontAwesomeIcon icon={faDownload} />
              </Button>
            </span>
            &nbsp;&nbsp;

          </div>
        ),
        filterable: false,
        sortable: false,
        minWidth: 100,
      },
    ].filter(Boolean);
    return (
      <>
        <LoadingSpinner isLoading={spinnerLoading} overlayHeight='25rem' />
        {!user.is_orsan && !isIntermediary && (
          <div
            style={{
              display: 'grid', gridTemplateColumns: '65px 400px', gridGap: '3px 8px',
              padding: '0 0 16px 16px', alignItems: 'center',
            }}
          >
            <div className="orsan-label">Nombre:</div>
            <div className="orsan-value">{user.organization_name}</div>
            <div className="orsan-label">RUT:</div>
            <div className="orsan-value">{user.organization_rut}</div>
          </div>
        )}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto auto',
            gridGap: '10px',
            marginBottom: '6px',
          }}
        >
          <div style={{ gridColumnStart: 2 }}>
            <DownloadBtn
              id="xlsx-download"
              text="XLSX"
              tipText="Descargar Planilla Excel"
              url={client ? `${xlsDownloadUrl}?client_id=${client.id}` : xlsDownloadUrl}
            />
          </div>
          <div style={{ gridColumnStart: 3 }}>
            <DownloadBtn
              id="pdf-download"
              text="PDF"
              tipText="Descargar PDF"
              url={client ? `${pdfDownloadUrl}?client_id=${client.id}` : pdfDownloadUrl}
            />
          </div>
        </div>
        <BaseTable
          tableRef={this.tableRef}
          columns={columns}
          data={creditPolicies.data}
          filterable
          loading={loading}
          onFetchData={this.fetchStrategy}
          onFilteredChange={this.onFilteredChange}
          pages={creditPolicies.pages}
          resultCount={creditPolicies.count}
          showPagination
          style={{ maxHeight: '63vh', opacity: spinnerLoading ? '0' : '1' }}
        />
        {/* Para que textos de la tabla no aparezcan sobre el spinner se aplica opacity: 0 */}
      </>
    );
  }
}

CreditPolicyList.propTypes = {
  dispatch: PropTypes.func.isRequired,
  creditPolicies: PropTypes.shape({
    data: PropTypes.array,
    pages: PropTypes.number,
    count: PropTypes.number,
  }).isRequired,
  poliza: PropTypes.shape({
    monedas: PropTypes.array,
    productos: PropTypes.array,
  }).isRequired,
  user: PropTypes.shape({
    organization: PropTypes.number,
    organization_name: PropTypes.string,
    organization_rut: PropTypes.string,
    is_orsan: PropTypes.bool,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  loginIdToken: PropTypes.string.isRequired,
  client: PropTypes.shape({
    id: PropTypes.number,
  }),
};

CreditPolicyList.defaultProps = {
  client: null,
};

const mapStateToProps = state => ({
  creditPolicies: state.credito.list.creditPolicies,
  loading: state.credito.loading,
  poliza: state.poliza,
  user: state.login.user,
  loginIdToken: state.login.id_token,
});

export default connect(mapStateToProps)(CreditPolicyList);
